import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";

import { SimpleToolbar } from "../../components/layout/Toolbar";
import { CustomButton } from "../../components/common/customButton";
import { renderSubTitle } from "../../components/renderComponents";
import { ContactDetail } from "../contacts/Contact";
import SourceDetail from "./Source";
import type { Contact, Deal } from "../../types/deal";
import { apiGet, apiPost } from "../../services/genesisApi/httpsMethods";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { buildPipelineColumnDefs } from "../pipeline/pipelineColumnDefs";
import "../../assets/css/AGGrid-custom.css";

ModuleRegistry.registerModules([AllCommunityModule]);

interface SourceWrapperProps {
  id?: number;
}

const SourceWrapper: FC<SourceWrapperProps> = ({ id }) => {
  const navigate = useNavigate();
  const params = useParams<{ sourceId?: string }>();
  const sourceId =
    id ?? (params.sourceId ? parseInt(params.sourceId) : undefined);
  const gridRef = useRef<AgGridReact>(null);
  const lists = useReferenceLists();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [dealsLoading, setDealsLoading] = useState(true);

  useEffect(() => {
    const fetchContacts = async () => {
      if (!sourceId) return;
      try {
        const response = await apiPost<Contact[]>("contacts/search", {
          source_id: sourceId,
        });
        if (response && response.success && response.data) {
          setContacts(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch contacts", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContacts();
  }, [sourceId]);

  useEffect(() => {
    const fetchDeals = async () => {
      if (!sourceId) return;
      try {
        const response = await apiGet<Deal[]>(`deals?source_id=${sourceId}`);
        if (response && response.success && response.data) {
          setDeals(response.data);
        } else {
          setDeals([]);
        }
      } catch (error) {
        console.error("Failed to fetch deals for source", error);
        setDeals([]);
      } finally {
        setDealsLoading(false);
      }
    };
    fetchDeals();
  }, [sourceId]);

  const pipelineColumnDefs: ColDef[] = useMemo(
    () => buildPipelineColumnDefs({ editMode: false, lists }),
    [lists],
  );

  if (!sourceId) {
    return <div>Source ID not found.</div>;
  }

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Box sx={{ position: "sticky", zIndex: 10 }}>
        <SimpleToolbar>
          <CustomButton
            handleButtonPress={() => {
              navigate(`/sources/${sourceId}/edit`);
            }}
            isButtonDisabled={false}
            text="Edit Source"
            isLoading={false}
            isLoadingText="Loading"
            solid={false}
            widthOverride="110px"
            data-position="right"
          />
        </SimpleToolbar>
      </Box>

      <Box sx={{ padding: 2, marginBottom: 0, zIndex: 100 }}>
        <SourceDetail sourceId={sourceId} isEdit={true} />

        {renderSubTitle("Contacts")}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2,
            marginTop: 1,
          }}
        >
          {contacts.length === 0 && !loading && (
            <Typography
              variant="subtitle1"
              sx={{ gridColumn: "span 3", marginBottom: 2 }}
            >
              No contacts found for this source.
            </Typography>
          )}
          {contacts.map((contact) => (
            <ContactDetail
              key={contact.id}
              contact={contact}
              isEdit={false}
              isDealPage={true}
            />
          ))}
        </Box>

        {renderSubTitle("Deals")}
        <Box sx={{ width: "100%", height: 520, marginTop: 1 }}>
          {deals.length === 0 && !dealsLoading ? (
            <Typography variant="subtitle1" sx={{ marginBottom: 2 }}>
              No deals found for this source.
            </Typography>
          ) : null}
          <div
            className="ag-theme-alpine"
            style={{ width: "100%", height: "100%" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={deals}
              columnDefs={pipelineColumnDefs}
              singleClickEdit={false}
              defaultColDef={{ flex: 0, resizable: true, filter: true }}
              rowHeight={30}
              getRowId={({ data }) => data?.id?.toString()}
            />
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default SourceWrapper;
